import React, { useState } from "react";
import { CircleStop, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import JobsRepository from "../../repositories/JobsRepository";
import { JobRequest } from "../../models/Jobs/JobRequest";
import JobInfoView from "./JobInfoView";

interface StopJobButtonProps {
  job: JobRequest;
  /** Called once the server has taken the request, to refresh whatever lists the job. */
  onStopped?: () => Promise<unknown> | void;
}

/**
 * Asks a running deletion to stop, after a confirmation.
 */
/*
 * The button only sends the request. The server finishes the item in progress, keeps what was deleted and ends the
 * job Stopped on its own, so the list shows "Stopping…" until its next refresh after that.
 */
const StopJobButton: React.FC<StopJobButtonProps> = ({ job, onStopped }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const jobsRepository = new JobsRepository();

  const confirm = async () => {
    setSubmitting(true);
    try {
      await jobsRepository.stopJob(job.id);
      toast.success("Stopping. The job ends after the item it is working on.");
      setOpen(false);
      await onStopped?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to stop the job.", { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Stop"
        title="Stop"
        className="text-destructive shrink-0"
        onClick={() => setOpen(true)}
      >
        <CircleStop className="size-4" />
      </Button>
      <Dialog open={open} onOpenChange={(next) => !next && !submitting && setOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CircleStop className="size-4" />
              Stop this job?
            </DialogTitle>
          </DialogHeader>
          <Separator />
          <div className="flex flex-col gap-2 text-sm">
            <p>It stops after the item it is working on. Nothing it has already deleted is put back.</p>
            <p className="text-muted-foreground">
              A deletion from an uploaded list then publishes its report, listing every id it did not delete.
            </p>
            <JobInfoView jobRequest={job} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirm} disabled={submitting}>
              {submitting && <Loader2 className="size-4 animate-spin" />}
              Stop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StopJobButton;
